import { colors } from '@federa/components/css-refs.colors';
import { typography } from '@federa/components/css-refs.typography';
import { spacing } from '@federa/components/css-refs.spacing';
import { motion, opacity, borderRadius } from '@federa/components/css-refs.effects';
import { SemanticIcon } from '@federa/components/icons/SemanticIcon';
import SharedEmptyState from '@federa/components/views/EmptyState';
import { VStack, HStack } from '@federa/components/layout/Stack';
import { SkeletonLine, SkeletonCircle } from '@federa/components/primitives/Skeleton';
import { SelectableButton } from '@federa/components/primitives/SelectableButton';
import { Button } from '@federa/components/primitives/Button';
import { MUTED_LABEL_SECONDARY, TEXT_SM_SECONDARY } from '@federa/components/common/layout-styles';
import { TEXT_TRUNCATE_STYLE } from '@federa/const/style-text-presets';
import { BORDER_MUTED } from '@federa/components/common/style-primitives';
import { createBackgroundHoverHandlers } from '@federa/components/common/event-handlers';
import { ARIA_ROLES } from '@federa/components/common/aria-tokens';
import { FULL_HEIGHT_STYLE, FULL_WIDTH_STYLE } from '@federa/const/style-size-presets';
import { BUTTON_PILL_PADDING, buildSymmetricPadding } from '@federa/utils/styles/style-padding-helpers';
import type { CSSProperties } from 'react';
import type { ChatRoomStruct } from '@federa/types/conversations-chat-structs';

const SKELETON_BG: CSSProperties = { backgroundColor: colors.background.tertiary };
const SKELETON_REVERSE_ROW: CSSProperties = { flexDirection: 'row-reverse' };
const EMPTY_STATE_ICON_WRAPPER: CSSProperties = { marginBottom: spacing[4], opacity: opacity.light };
const UPLOAD_BAR_OUTER: CSSProperties = {
	marginBottom: spacing[3],
	padding: spacing[3],
	backgroundColor: colors.background.coolNeutral,
	borderRadius: spacing[2],
	border: BORDER_MUTED,
};
const UPLOAD_BAR_HEADER_ROW: CSSProperties = { marginBottom: spacing[2] };
const UPLOAD_LABEL_TEXT: CSSProperties = {
	...TEXT_SM_SECONDARY,
	fontWeight: typography.semanticWeights.action,
};
const UPLOAD_TRACK: CSSProperties = {
	...FULL_WIDTH_STYLE,
	height: spacing[2],
	backgroundColor: colors.background.tertiary,
	borderRadius: spacing[1],
	overflow: 'hidden',
};
const CONVERSATIONS_DROPDOWN: CSSProperties = {
	marginLeft: spacing[3],
	marginRight: spacing[3],
	marginTop: spacing[2],
	marginBottom: spacing[2],
	borderRadius: borderRadius.xl,
	maxHeight: spacing[80],
	overflowY: 'auto',
	borderColor: colors.border.muted,
	backgroundColor: colors.background.primary,
};
const CONVERSATIONS_HEADER: CSSProperties = {
	...BUTTON_PILL_PADDING,
	borderColor: colors.border.muted,
	backgroundColor: colors.background.coolNeutral,
	...MUTED_LABEL_SECONDARY,
	fontWeight: typography.semanticWeights.action,
};
const CONVERSATIONS_EMPTY_STATE: CSSProperties = {
	...buildSymmetricPadding(4, 6),
	...TEXT_SM_SECONDARY,
};
const CONVERSATIONS_LIST_PADDING: CSSProperties = { paddingTop: spacing[1], paddingBottom: spacing[1] };

function buildConversationItemStyle(isSelected: boolean): CSSProperties {
	return {
		...buildSymmetricPadding(4, 3),
		backgroundColor: isSelected ? colors.background.coolNeutral : 'transparent',
		borderLeft: isSelected ? `${spacing[0.75]} solid ${colors.brand.primary}` : `${spacing[0.75]} solid transparent`,
		borderColor: colors.border.muted,
	};
}

function buildConversationTitleStyle(isSelected: boolean): CSSProperties {
	return {
		fontSize: typography.fontSize.sm,
		fontWeight: isSelected ? typography.semanticWeights.action : typography.semanticWeights.body,
		color: colors.text.primary,
		...TEXT_TRUNCATE_STYLE,
	};
}

const CONVERSATION_LAST_MESSAGE: CSSProperties = {
	...MUTED_LABEL_SECONDARY,
	opacity: opacity.strong,
	marginTop: spacing[0.5],
	...TEXT_TRUNCATE_STYLE,
};

function buildProgressFillStyle(progress: number): CSSProperties {
	return {
		width: `${progress}%`,
		...FULL_HEIGHT_STYLE,
		backgroundColor: colors.brand.primary,
		transition: `width ${motion.duration.slow} ${motion.easing.standard}`,
	};
}

function ConversationItem({ room, isSelected, onSelect }: { room: ChatRoomStruct; isSelected: boolean; onSelect: () => void }) {
	const hoverHandlers = createBackgroundHoverHandlers({
		hoverColor: colors.background.coolNeutral,
		condition: isSelected,
	});
	return (
		<SelectableButton
			isSelected={isSelected}
			onSelect={onSelect}
			className="w-full text-left transition border-b"
			role={ARIA_ROLES.option}
			selectionAria="selected"
			style={buildConversationItemStyle(isSelected)}
			hoverHandlers={hoverHandlers}
		>
			<div style={buildConversationTitleStyle(isSelected)}>{room.title || 'Conversation'}</div>
			{room.lastMessage && <div style={CONVERSATION_LAST_MESSAGE}>{room.lastMessage}</div>}
		</SelectableButton>
	);
}

export function ConversationListDropdown({
	conversations,
	currentConversationId,
	onSelectConversation,
}: {
	conversations: ChatRoomStruct[];
	currentConversationId: string | undefined;
	onSelectConversation: (room: ChatRoomStruct) => void;
}) {
	return (
		<div className="border shadow-sm" role={ARIA_ROLES.listbox} aria-label="Conversation list" style={CONVERSATIONS_DROPDOWN}>
			<div className="border-b" style={CONVERSATIONS_HEADER}>
				Conversations
			</div>
			{conversations.length === 0 ? (
				<div className="text-center" style={CONVERSATIONS_EMPTY_STATE}>
					No conversations yet
				</div>
			) : (
				<div style={CONVERSATIONS_LIST_PADDING}>
					{conversations.map((room) => (
						<ConversationItem
							key={room.id}
							room={room}
							isSelected={room.id === currentConversationId}
							onSelect={() => onSelectConversation(room)}
						/>
					))}
				</div>
			)}
		</div>
	);
}

const LOAD_OLDER_ROW: CSSProperties = { marginBottom: spacing[3] };

/**
 * The scrollback control: one group of older messages per press, rendered ABOVE the list it prepends to.
 *
 * Renders NOTHING when there is nothing older -- a permanently disabled control at the top of every short conversation
 * would read as a feature that is broken rather than one that is finished. `hasOlder` is the server's answer (a page
 * shorter than the group it asked for), not a count taken off the rendered list.
 */
export function LoadOlderMessages({ hasOlder, loading, onLoadOlder }: { hasOlder: boolean; loading: boolean; onLoadOlder: () => void }) {
	if (!hasOlder) return null;
	return (
		<HStack justify="center" style={LOAD_OLDER_ROW}>
			<Button variant="ghost" size="sm" loading={loading} disabled={loading} onClick={onLoadOlder}>
				Load earlier messages
			</Button>
		</HStack>
	);
}

export function MessagesSkeleton() {
	return (
		<VStack gap={spacing[4]}>
			{/* Skeleton for assistant message */}
			<HStack gap={spacing[3]} align="start">
				<SkeletonCircle size={spacing[7]} style={SKELETON_BG} />
				<SkeletonLine width="70%" height={spacing[15]} radius={borderRadius.xl} style={SKELETON_BG} />
			</HStack>
			{/* Skeleton for user message */}
			<HStack gap={spacing[3]} align="start" style={SKELETON_REVERSE_ROW}>
				<SkeletonCircle size={spacing[7]} style={SKELETON_BG} />
				<SkeletonLine width="50%" height={spacing[10]} radius={borderRadius.xl} style={SKELETON_BG} />
			</HStack>
		</VStack>
	);
}

export function EmptyMessagesState() {
	return (
		<SharedEmptyState
			title="How can I help you?"
			description="Ask me questions about properties, your listings, or anything else related to FEDERA."
			icon={
				<div style={EMPTY_STATE_ICON_WRAPPER}>
					<SemanticIcon name="chat" size="2xl" color="secondary" />
				</div>
			}
			className="h-full"
		/>
	);
}

export function UploadProgressBar({ progress }: { progress: number }) {
	return (
		<div style={UPLOAD_BAR_OUTER}>
			<HStack justify="space-between" align="center" style={UPLOAD_BAR_HEADER_ROW}>
				<span style={UPLOAD_LABEL_TEXT}>Uploading files...</span>
				<span style={TEXT_SM_SECONDARY}>{progress}%</span>
			</HStack>
			<div style={UPLOAD_TRACK}>
				<div style={buildProgressFillStyle(progress)} />
			</div>
		</div>
	);
}
